<template>
    <v-dialog v-model="isActive" fullscreen persistent eager>
        <div class="shipment-close-payments-page">
            <div class="shipment-close-payments-page__header">
                <v-btn
                    icon
                    data-test-id="shipment-close-payments--close-btn"
                    @click="closeHandle"
                >
                    <v-icon>mdi-close</v-icon>
                </v-btn>
                <h4 class="shipment-close-payments-page__title">
                    {{ $t('pages.shipments.addPayments') }}
                </h4>
            </div>
            <div class="shipment-close-payments-page__body">
                <div
                    v-if="documents.length === 0"
                    class="shipment-close-payments-page__empty"
                >
                    {{ $t('pages.shipments.noDocumentsSelected') }}
                </div>

                <div
                    v-for="document in documents"
                    :key="document.shipment_item_id"
                    class="shipment-close-payments-page__document"
                >
                    <div class="shipment-close-payments-page__document-header">
                        <div
                            class="shipment-close-payments-page__document-info"
                        >
                            <div
                                class="shipment-close-payments-page__document-title"
                            >
                                {{ $t('pages.shipments.document') }} #{{
                                    document.shipment_item_id
                                }}
                            </div>
                            <div
                                class="shipment-close-payments-page__document-details"
                            >
                                <span v-if="document.wig">
                                    {{ $t('pages.shipments.barcode') }}:
                                    {{ document.wig.barcode || '-' }}
                                </span>
                                <span v-if="document.wig?.client">
                                    {{ $t('pages.shipments.client') }}:
                                    {{ document.wig.client }}
                                </span>
                                <span v-if="document.wig?.usmanager">
                                    {{ $t('pages.shipments.usManager') }}:
                                    {{ document.wig.usmanager || '-' }}
                                </span>
                            </div>
                        </div>
                        <div
                            class="shipment-close-payments-page__document-amount"
                        >
                            <div
                                class="shipment-close-payments-page__amount-details"
                            >
                                <span
                                    class="shipment-close-payments-page__amount-label"
                                >
                                    {{ $t('pages.shipments.totalAmount') }}:
                                </span>
                                <span
                                    class="shipment-close-payments-page__amount-value"
                                >
                                    {{ formatAmount(document.price || 0) }}
                                </span>
                            </div>
                            <div
                                class="shipment-close-payments-page__amount-details"
                            >
                                <span
                                    class="shipment-close-payments-page__amount-label"
                                >
                                    {{ $t('pages.shipments.totalPaid') }}:
                                </span>
                                <span
                                    class="shipment-close-payments-page__amount-value"
                                >
                                    {{ formatAmount(document.total_paid || 0) }}
                                </span>
                            </div>
                            <div
                                class="shipment-close-payments-page__amount-details"
                            >
                                <span
                                    class="shipment-close-payments-page__amount-label"
                                >
                                    {{ $t('pages.shipments.remaining') }}:
                                </span>
                                <span
                                    class="shipment-close-payments-page__amount-value"
                                >
                                    {{ formatAmount(document.remaining || 0) }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="shipment-close-payments-page__payments-section">
                        <div
                            class="shipment-close-payments-page__payments-list"
                        >
                            <div
                                v-for="(payment, index) in getProcessedPayments(
                                    document.payments
                                )"
                                :key="payment.local_id || index"
                                class="shipment-close-payments-page__list-item"
                            >
                                <div
                                    class="shipment-close-payments-page__entry"
                                    :style="{ backgroundColor: payment.color }"
                                >
                                    <v-btn
                                        color="#B05454"
                                        class="shipment-close-payments-page__remove"
                                        @click="
                                            removePayment(document, payment)
                                        "
                                    >
                                        <v-icon
                                            class="shipment-close-payments-page__remove-icon"
                                            small
                                        >
                                            mdi mdi-close-circle-outline
                                        </v-icon>
                                    </v-btn>

                                    <div>
                                        <v-select
                                            v-if="!isCard(payment.payment_type)"
                                            v-model="payment.payment_type"
                                            :items="paymentTypes"
                                            :item-text="
                                                getTranslationForPaymentTypes
                                            "
                                            item-value="payment_type"
                                            :label="
                                                $t(
                                                    'pages.payments.selectPaymentType'
                                                )
                                            "
                                            outlined
                                            dense
                                            class="payment-field"
                                            @change="
                                                handlePaymentTypeChange(
                                                    document,
                                                    payment
                                                )
                                            "
                                        ></v-select>
                                        <div
                                            v-else
                                            class="payment-field__block"
                                        >
                                            <small class="small">{{
                                                $t('pages.payments.paymentType')
                                            }}</small>
                                            {{
                                                getPaymentTypeByType(
                                                    payment.payment_type
                                                )
                                            }}
                                        </div>
                                    </div>

                                    <component
                                        :is="
                                            getPaymentFormComponent(
                                                payment.payment_type
                                            )
                                        "
                                        v-bind="payment"
                                        :hide-fields="true"
                                        @update-payment="
                                            updatePayment(
                                                document,
                                                payment,
                                                $event
                                            )
                                        "
                                    ></component>
                                </div>
                            </div>

                            <div
                                v-if="document.payments.length === 0"
                                class="shipment-close-payments-page__no-payments"
                            >
                                {{ $t('pages.shipments.noPaymentsAdded') }}
                            </div>
                        </div>

                        <div
                            class="shipment-close-payments-page__payments-actions"
                        >
                            <v-btn
                                color="#37BC9B"
                                dark
                                class="shipment-close-payments-page__add-button"
                                @click="openAddPaymentModal(document)"
                            >
                                {{ $t('pages.payments.addPayment') }}
                            </v-btn>
                        </div>

                        <div
                            class="shipment-close-payments-page__document-validation"
                        >
                            <div
                                class="shipment-close-payments-page__validation-info"
                            >
                                <span
                                    class="shipment-close-payments-page__validation-label"
                                >
                                    {{ $t('pages.shipments.totalPayments') }}:
                                </span>
                                <span
                                    class="shipment-close-payments-page__validation-amount"
                                    :class="{
                                        'shipment-close-payments-page__validation-amount--valid':
                                            isDocumentValid(document),
                                        'shipment-close-payments-page__validation-amount--invalid':
                                            !isDocumentValid(document) &&
                                            getDocumentTotalPayments(document) >
                                                0,
                                    }"
                                >
                                    {{
                                        formatAmount(
                                            getDocumentTotalPayments(document)
                                        )
                                    }}
                                </span>
                            </div>
                            <div
                                v-if="
                                    !isDocumentValid(document) &&
                                    getDocumentTotalPayments(document) > 0
                                "
                                class="shipment-close-payments-page__validation-error"
                            >
                                <v-icon small color="error"
                                    >mdi-alert-circle</v-icon
                                >
                                <span
                                    class="shipment-close-payments-page__validation-error_span"
                                    >{{
                                        getDocumentValidationError(document)
                                    }}</span
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="shipment-close-payments-page__footer">
                <v-btn color="grey" dark @click="handleClose">
                    {{ $t('pages.shipments.cancel') }}
                </v-btn>
                <v-btn
                    color="#37BC9B"
                    :dark="isAllDocumentsValid"
                    :disabled="!isAllDocumentsValid"
                    :loading="isSubmitting"
                    @click="onConfirm"
                >
                    {{ $t('pages.shipments.confirmAndClose') }}
                </v-btn>
            </div>
        </div>

        <!-- Payment add modal -->
        <ShipmentPaymentAddModal
            v-if="selectedDocumentForPayment"
            :document="selectedDocumentForPayment"
            @payment-added="handlePaymentAdded"
            @close="closePaymentModal"
        />
    </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import modalsMixin from '@/mixins/modals.mixin'
import popUpTypes from '@/types/pop-up-types'
import languageMixin from '@/mixins/language.mixin'
import { getterTypes as getterTypesModals } from '@/store/modules/modals'
import { actionTypes as modalNotificationActionTypes } from '@/store/modules/modal-notifications'
import {
    paymentsV2ActionTypes,
    paymentsV2GetterTypes,
} from '@/store/modules/payments_v2/types'
import ShipmentPaymentAddModal from '@/components/shipments/shipment-payment-add-modal.vue'
import PaymentCashForm from '@/components/payments/payment-cash-form.vue'
import PaymentCardForm from '@/components/payments/payment-card-form.vue'
import PaymentCheckForm from '@/components/payments/payment-check-form.vue'
import PaymentTransferForm from '@/components/payments/payment-transfer-form.vue'
import ShipmentPaymentCardForm from '@/components/shipments/shipment-payment-card-form.vue'
import { paymentSort } from '@/helpers/payment-sort.utils'
import shortid from 'shortid'

const { shipmentClosePayments } = popUpTypes

export default {
    name: 'ShipmentClosePaymentsPage',
    components: {
        ShipmentPaymentAddModal,
        PaymentCashForm,
        PaymentCheckForm,
        PaymentCardForm,
        PaymentTransferForm,
        ShipmentPaymentCardForm,
    },
    mixins: [modalsMixin, languageMixin],
    data() {
        return {
            selfName: shipmentClosePayments,
            isLoading: false,
            isSubmitting: false,
            documents: [],
            selectedDocumentForPayment: null,
        }
    },
    computed: {
        ...mapGetters('modals', {
            getActiveModals: getterTypesModals.getModals,
        }),
        paymentTypes() {
            if (!this.isActive) return []
            const allTypes =
                this.$store.getters[
                    'paymentsV2/' + paymentsV2GetterTypes.GET_PAYMENT_TYPES
                ] || []
            return allTypes.filter((type) => type.payment_type !== 100)
        },
        shipmentId() {
            return this.extraData?.shipmentId
        },
        isAllDocumentsValid() {
            return this.documents.every((doc) => this.isDocumentValid(doc))
        },
    },
    watch: {
        isActive(newVal) {
            if (newVal) {
                this.initDocuments()
                this.fetchPaymentTypes()
                this.fetchBankCodes()
                return
            }

            this.resetForm()
        },
    },
    methods: {
        ...mapActions('paymentsV2', {
            fetchPaymentTypes: paymentsV2ActionTypes.FETCH_PAYMENT_TYPES,
            fetchBankCodes: paymentsV2ActionTypes.FETCH_BANK_CODES,
        }),
        ...mapActions('modalNotifications', {
            showError: modalNotificationActionTypes.error,
            showSuccess: modalNotificationActionTypes.success,
        }),
        initDocuments() {
            const items = this.extraData?.items || []
            this.documents = items.map((item) => ({
                shipment_item_id: item.id || item.wig_id || item.wig?.id,
                price: item.price || 0,
                remaining: item.remaining || 0,
                total_paid: item.total_paid || 0,
                wig: item.wig || {},
                payments: [],
            }))
        },
        getDocumentTotalPayments(document) {
            return document.payments.reduce((sum, payment) => {
                const amount = parseFloat(payment.amount || 0)
                return sum + (isNaN(amount) ? 0 : amount)
            }, 0)
        },
        isDocumentValid(document) {
            const totalPayments = this.getDocumentTotalPayments(document)
            const requiredAmount = parseFloat(document.remaining || 0)
            return totalPayments <= requiredAmount + 0.01
        },
        getDocumentValidationError(document) {
            const totalPayments = this.getDocumentTotalPayments(document)
            const requiredAmount = parseFloat(document.remaining || 0)
            const diff = totalPayments - requiredAmount

            if (diff > 0.01) {
                return this.$t('pages.shipments.overpaymentError', {
                    amount: this.formatAmount(Math.abs(diff)),
                })
            }
            return ''
        },
        openAddPaymentModal(document) {
            this.selectedDocumentForPayment = { ...document }
        },
        handlePaymentAdded(paymentData) {
            if (!this.selectedDocumentForPayment) return

            const processedPayment = {
                ...paymentData,
                local_id: paymentData.local_id || shortid(),
            }

            const docIndex = this.documents.findIndex(
                (doc) =>
                    doc.shipment_item_id ===
                    this.selectedDocumentForPayment.shipment_item_id
            )

            if (docIndex !== -1) {
                this.documents[docIndex].payments.push(processedPayment)
                const sortedPayments = paymentSort([
                    ...this.documents[docIndex].payments,
                ])
                this.$set(this.documents[docIndex], 'payments', sortedPayments)
            }
        },
        removePayment(document, payment) {
            const docIndex = this.documents.findIndex(
                (doc) => doc.shipment_item_id === document.shipment_item_id
            )

            if (docIndex === -1) return

            let filteredPayments
            if (this.isCard(payment.payment_type) && payment.sub_id) {
                filteredPayments = this.documents[docIndex].payments.filter(
                    (p) => p.sub_id !== payment.sub_id
                )
                const sortedPayments = paymentSort([...filteredPayments])
                this.$set(this.documents[docIndex], 'payments', sortedPayments)
                return
            }

            filteredPayments = this.documents[docIndex].payments.filter(
                (p) => p.local_id !== payment.local_id
            )
            const sortedPayments = paymentSort([...filteredPayments])
            this.$set(this.documents[docIndex], 'payments', sortedPayments)
        },
        isCard(payment_type) {
            return [4, 5, 6, 7, 8, 10, 11].includes(payment_type)
        },
        getProcessedPayments(payments) {
            const sortedPayments = paymentSort([...payments])

            let currentColor = '#F9F9FF'
            let lastType = null

            return sortedPayments.map((payment) => {
                if (payment.payment_type !== lastType) {
                    currentColor =
                        currentColor === '#F9F9FF' ? '#FFF9F9' : '#F9F9FF'
                    lastType = payment.payment_type
                }

                return { ...payment, color: currentColor }
            })
        },
        getPaymentTypeByType(paymentType) {
            const type = this.paymentTypes.find(
                (pt) => pt.payment_type === paymentType
            )
            if (!type) return ''
            return this.getTranslationForPaymentTypes(type)
        },
        handlePaymentTypeChange(document, payment) {
            const docIndex = this.documents.findIndex(
                (doc) => doc.shipment_item_id === document.shipment_item_id
            )
            if (docIndex === -1) return

            const paymentIndex = this.documents[docIndex].payments.findIndex(
                (p) => p.local_id === payment.local_id
            )
            if (paymentIndex === -1) return

            this.$set(
                this.documents[docIndex].payments[paymentIndex],
                'payment_type',
                payment.payment_type
            )
            this.$set(
                this.documents[docIndex].payments[paymentIndex],
                'amount',
                ''
            )
            this.$set(
                this.documents[docIndex].payments[paymentIndex],
                'due_date',
                ''
            )
            const sortedPayments = paymentSort([
                ...this.documents[docIndex].payments,
            ])
            this.$set(this.documents[docIndex], 'payments', sortedPayments)
        },
        updatePayment(document, payment, newData) {
            const docIndex = this.documents.findIndex(
                (doc) => doc.shipment_item_id === document.shipment_item_id
            )
            if (docIndex === -1) return

            const paymentIndex = this.documents[docIndex].payments.findIndex(
                (p) => p.local_id === payment.local_id
            )
            if (paymentIndex === -1) return

            this.$set(this.documents[docIndex].payments, paymentIndex, {
                ...this.documents[docIndex].payments[paymentIndex],
                ...newData,
            })
        },
        getPaymentFormComponent(type_id) {
            if (type_id === 1) {
                return 'PaymentCheckForm'
            }
            if (type_id === 2) {
                return 'PaymentCashForm'
            }
            if (type_id === 9) {
                return 'PaymentTransferForm'
            }
            if (this.isCard(type_id)) {
                return 'ShipmentPaymentCardForm'
            }
        },
        getTranslationForPaymentTypes(item) {
            const translatedKey = `pages.payments.${item.payment_name}`
            const translatedText = this.$t(translatedKey)

            if (translatedText.includes('Translation not found for key')) {
                return item.payment_name
            }

            return translatedText
        },
        formatAmount(amount) {
            return new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(amount)
        },
        formatDate(date) {
            if (!date) return ''

            if (typeof date === 'string' && date.includes('-')) {
                const [year, month, day] = date.split('-')
                return `${day}/${month}/${year}`
            }

            return date
        },
        formatPaymentsForApi() {
            return this.documents.map((document) => {
                const paymentsByType = {}

                document.payments.forEach((payment) => {
                    const paymentType = payment.payment_type.toString()
                    if (!paymentsByType[paymentType]) {
                        paymentsByType[paymentType] = {
                            payments: [],
                        }
                    }

                    const formattedPayment = {
                        payment_type: paymentType,
                        amount: parseFloat(payment.amount || 0).toFixed(2),
                    }

                    if (payment.due_date) {
                        formattedPayment.due_date = this.formatDateForApi(
                            payment.due_date
                        )
                    }

                    if (payment.check_number) {
                        formattedPayment.check_number = payment.check_number
                    }
                    if (payment.bank_code) {
                        formattedPayment.bank_code = String(payment.bank_code)
                    }
                    if (payment.branch_number) {
                        formattedPayment.branch_number = payment.branch_number
                    }
                    if (payment.bank_account_number) {
                        formattedPayment.bank_account_number =
                            payment.bank_account_number
                    }

                    paymentsByType[paymentType].payments.push(formattedPayment)
                })

                return {
                    shipment_item_id: document.shipment_item_id,
                    payments: paymentsByType,
                }
            })
        },
        formatDateForApi(date) {
            if (!date) return ''

            if (typeof date === 'string' && date.includes('-')) {
                const [year, month, day] = date.split('-')
                return `${day}/${month}/${year}`
            }

            const d = new Date(date)
            if (isNaN(d.getTime())) return ''

            const day = String(d.getDate()).padStart(2, '0')
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const year = d.getFullYear()
            return `${day}/${month}/${year}`
        },
        closePaymentModal() {
            this.selectedDocumentForPayment = null
        },
        resetForm() {
            this.documents = []
            this.selectedDocumentForPayment = null
            this.isSubmitting = false
        },
        handleClose() {
            this.resetForm()
            this.closeHandle()
        },
        async onConfirm() {
            if (!this.isAllDocumentsValid) return

            this.isSubmitting = true

            try {
                const formattedData = this.formatPaymentsForApi()
                const confirmCallback = this.extraData?.onConfirm
                if (confirmCallback && typeof confirmCallback === 'function') {
                    await confirmCallback({
                        items: formattedData,
                    })
                }
                this.handleClose()
            } catch (error) {
                const errorMessage =
                    error?.response?.data?.message ||
                    this.$t('pages.shipments.closeError')
                this.showError(errorMessage)
            } finally {
                this.isSubmitting = false
            }
        },
    },
}
</script>

<style lang="scss">
.shipment-close-payments-page {
    position: absolute;
    bottom: 0;
    background: #fff;
    width: 100%;
    height: calc(100% - 40px);
    padding: 75px 15px 80px;
    display: flex;
    flex-direction: column;

    &__header {
        width: 100%;
        position: absolute;
        height: 65px;
        top: 0;
        left: 0;
        background: #fff;
        z-index: 2;
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        padding: 0 15px;
    }

    &__title {
        margin-left: 10px;
        font-size: 18px;
        font-weight: 500;
    }

    &__body {
        position: relative;
        z-index: 1;
        flex: 1;
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    &__empty {
        text-align: center;
        padding: 40px;
        color: #999;
    }

    &__document {
        width: 100%;
        padding: 24px 0 0 0;
        box-shadow: 0 10px 10px rgba(0, 0, 0, 0.1);
        border: 1px #967adc solid;
        border-radius: 16px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        margin-bottom: 24px;
    }

    &__document-header {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 16px;
        padding: 0 16px;
        flex-wrap: wrap;
        gap: 12px;
    }

    &__document-info {
        flex: 1;
        min-width: 200px;
    }

    &__document-title {
        font-size: 16px;
        font-weight: 600;
        margin-bottom: 8px;
    }

    &__document-details {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 14px;
        color: #666;
    }

    &__document-amount {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    }

    &__amount-details {
        width: fit-content;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
    }

    &__amount-label {
        font-size: 12px;
        color: #666;
    }

    &__amount-value {
        font-size: 18px;
        font-weight: 600;
        color: #333;
    }

    &__payments-section {
        width: 100%;
        margin-top: 16px;
    }

    &__payments-list {
        width: 100%;
    }

    &__list-item {
        width: 100%;
        border-top: 1px solid #c3c3c3;

        &:last-child {
            border-bottom: 1px solid #c3c3c3;
        }
    }

    &__remove {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
        aspect-ratio: 1 !important;
        min-width: 32px !important;
        height: 32px !important;
        margin-top: 4px;
        border-radius: 100% !important;
    }

    &__remove-icon {
        font-size: 24px !important;
    }

    &__entry {
        width: 100%;
        display: flex;
        gap: 16px;
        padding: 28px 34px 0 34px;
        background: #fff9f9;
    }

    &__no-payments {
        text-align: center;
        padding: 20px;
        color: #999;
        font-size: 14px;
    }

    &__payments-actions {
        padding: 16px 0;
        display: flex;
        justify-content: center;
    }

    &__add-button {
        width: 175px;
    }

    &__document-validation {
        padding: 12px 16px;
        background: #f5f5f5;
        margin-top: 12px;
        border-radius: 0 0 16px 16px;
    }

    &__validation-info {
        display: flex;
        justify-content: flex-start;
        gap: 8px;
        align-items: center;
        margin-bottom: 8px;
    }

    &__validation-label {
        font-weight: 500;
        font-size: 14px;
    }

    &__validation-amount {
        font-weight: 600;
        font-size: 16px;

        &--valid {
            color: #4caf50;
        }

        &--invalid {
            color: #f44336;
        }
    }

    &__validation-error {
        display: flex;
        align-items: center;
        gap: 6px;
        color: #f44336;
        font-size: 12px;
        margin: 0;
    }

    &__validation-error_span {
        margin: 0 !important;
    }

    &__footer {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 80px;
        background: #fff;
        box-shadow: 0 -5px 5px rgba(0, 0, 0, 0.065);
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 16px;
        gap: 12px;
        z-index: 2;
    }

    .payment-fields {
        width: 100%;
        transition: all 0.3s ease-in-out;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 16px;
    }

    .payment-field {
        width: 100%;
        max-width: 234px !important;
        min-width: 150px;
    }
}

.payment-field__block {
    display: flex;
    flex-direction: column;
    margin-bottom: 28px;
}

.small {
    font-size: 12px;
    color: #ababab;
    text-wrap: nowrap;
}
</style>
